import type { IgfdPaymentPlan, Restaurant } from "api-types";
import {
  Timestamp,
  documentId,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  FirestorePaymentPlanDocument,
  FirestoreRestaurantDocument,
  RestaurantDraft,
  RestaurantWithPaymentPlan,
} from "@dashboard/types";

export interface RestaurantApi {
  // given the current user's uid, fetches all restaurants that the user has access to
  fetchAccessibleRestaurants(uid: string): Promise<RestaurantWithPaymentPlan[]>;

  // Fetch the given restaurant and its payment plan
  // Useful for refreshing the restaurant state / payment plan
  fetchRestaurantAndPaymentPlan(
    restaurantUuid: string
  ): Promise<RestaurantWithPaymentPlan>;

  updateRestaurantDetails(
    restaurantUuid: string,
    draft: Partial<RestaurantDraft>
  ): Promise<void>;

  // Terms and conditions acceptance
  acceptTermsAndConditions(restaurantUuid: string): Promise<void>;

  fetchTermsAndConditionsAcceptance(restaurantUuid: string): Promise<boolean>;
}

// Note this logic also exists in the useCreateRestaurant hook
export const restaurantFromDoc = (
  docData: FirestoreRestaurantDocument
): Restaurant => {
  const { createdAt, ...rest } = docData as Restaurant & {
    createdAt: Timestamp;
  };
  return rest as Restaurant;
};

// orders by createdAt asc nulls first
const firestoreRestaurantDocComparator = (
  a: FirestoreRestaurantDocument,
  b: FirestoreRestaurantDocument
) => {
  if (a.createdAt && b.createdAt) {
    return a.createdAt - b.createdAt;
  }
  if (a.createdAt) {
    return 1;
  }
  if (b.createdAt) {
    return -1;
  }
  return 0;
};

export const restaurantPaymentPlanFromDoc = (
  docData: FirestorePaymentPlanDocument
): IgfdPaymentPlan => {
  // fields that are ommitted from the response
  const { restaurantRef, ...rest } = docData as FirestorePaymentPlanDocument;
  return rest as IgfdPaymentPlan;
};

/**
 * An implementation of the RestaurantApi that uses Firestore as the backend.
 */
export class FirestoreBackedRestaurantApi implements RestaurantApi {
  private readonly firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  public async fetchAccessibleRestaurants(
    uid: string
  ): Promise<RestaurantWithPaymentPlan[]> {
    const accessibleRestaurantUuids = await getDocs(
      query(this.firestoreRefs.managerAccessibleRestaurants(uid))
    ).then((querySnapshot) => querySnapshot.docs.map((doc) => doc.id));

    if (accessibleRestaurantUuids.length === 0) {
      // if the user has no restaurants, skip the next query, return an empty array
      return [];
    }

    // FIXME load restaurants in batches!
    // Currently no one should have access to more than 30 restaurants. Our limit from firestore is 30
    const limitedRestaurantUuids = accessibleRestaurantUuids.slice(0, 30);

    const allAccessibleRestaurantsPromise = getDocs(
      // FIXME this hits the limit of 30 for `in` clauses
      query(
        this.firestoreRefs.restaurants(),
        where(documentId(), "in", limitedRestaurantUuids)
      )
    );

    const allAccessiblePaymentPlansPromise = getDocs(
      query(
        this.firestoreRefs.paymentPlans(),
        where(documentId(), "in", limitedRestaurantUuids)
      )
    );

    const [allAccessibleRestaurants, allAccessiblePaymentPlans] =
      await Promise.all([
        allAccessibleRestaurantsPromise,
        allAccessiblePaymentPlansPromise,
      ]);

    // merge the restaurant and payment plan data
    return allAccessibleRestaurants.docs
      .map((doc) => doc.data() as FirestoreRestaurantDocument)
      .sort(firestoreRestaurantDocComparator)
      .map((doc) => {
        const restaurant = restaurantFromDoc(doc);
        // A payment plan doc is created transactionally with every restaurant,
        // so the match is guaranteed to exist.
        const paymentPlanDoc = allAccessiblePaymentPlans.docs.find(
          (planDoc) => planDoc.id === restaurant.uuid
        )!;
        const paymentPlan = restaurantPaymentPlanFromDoc(
          paymentPlanDoc.data() as FirestorePaymentPlanDocument
        );
        return {
          restaurant,
          paymentPlan,
        } as RestaurantWithPaymentPlan;
      });
  }

  public async fetchRestaurantAndPaymentPlan(
    restaurantUuid: string
  ): Promise<RestaurantWithPaymentPlan> {
    const restaurantPromise = getDoc(
      this.firestoreRefs.restaurant(restaurantUuid)
    ).then((doc) => doc.data() as FirestoreRestaurantDocument);

    const paymentPlanPromise = getDoc(
      this.firestoreRefs.paymentPlan(restaurantUuid)
    ).then((doc) => doc.data() as FirestorePaymentPlanDocument);

    const [restaurantDoc, paymentPlanDoc] = await Promise.all([
      restaurantPromise,
      paymentPlanPromise,
    ]);

    const restaurant = restaurantFromDoc(restaurantDoc);
    const paymentPlan = restaurantPaymentPlanFromDoc(paymentPlanDoc);

    return {
      restaurant,
      paymentPlan,
    } as RestaurantWithPaymentPlan;
  }

  public updateRestaurantDetails(
    restaurantUuid: string,
    draft: Partial<RestaurantDraft>
  ): Promise<void> {
    return setDoc(this.firestoreRefs.restaurant(restaurantUuid), draft, {
      merge: true,
    });
  }

  acceptTermsAndConditions(restaurantUuid: string): Promise<void> {
    return setDoc(
      this.firestoreRefs.restaurantTermsAcceptance(restaurantUuid),
      {
        acceptedTcId: "p7FO3rZL4E4OeANmSEc1", // Hardcoded since we have only one version of the terms and conditions
        createdAt: Timestamp.now(),
      },
      { merge: true }
    );
  }

  fetchTermsAndConditionsAcceptance(restaurantUuid: string): Promise<boolean> {
    return getDoc(
      this.firestoreRefs.restaurantTermsAcceptance(restaurantUuid)
    ).then((doc) => {
      return doc.exists();
    });
  }
}
